/**
 * Mueve un nodo a nuevas coordenadas y sincroniza las rutas de los cables
 * conectados (origen o destino). Devuelve una entrada lista para `useMapHistoryStore`
 * con el estado previo y posterior, permitiendo undo.
 *
 * Extraído del dragend handler de NodeMarkers para reutilizar entre:
 *   - Arrastre manual (drag en el mapa)
 *   - Actualización por GPS ("Poner aquí")
 *
 * @module utils/moverNodoConCables
 */

import type { AnyNode } from '@/types/nodes'
import type { PuntoRuta } from '@/types/fibers'

type CableLike = {
  id: string
  origen?: string
  origenId?: string
  destino?: string
  destinoId?: string
  ruta?: PuntoRuta[]
}

export interface MoverNodoDeps {
  updateNode: (node: AnyNode) => Promise<unknown>
  updateCable: (cable: any) => Promise<unknown>
  cables: CableLike[]
}

export interface MoverNodoResultado {
  nodeId: string
  before: { lat: number; lng: number }
  after: { lat: number; lng: number }
  cables: Array<{ id: string; beforeRuta: PuntoRuta[]; afterRuta: PuntoRuta[] }>
}

/**
 * Mueve el nodo y sus cables conectados. Si algún cable falla al actualizarse,
 * revierte la posición del nodo y propaga el error para que el llamador
 * reaccione (p. ej., rollback del marker).
 */
export async function moverNodoConCables(
  node: AnyNode,
  newLatLng: { lat: number; lng: number },
  deps: MoverNodoDeps
): Promise<MoverNodoResultado> {
  const before = { lat: node.lat, lng: node.lng }

  if (before.lat === newLatLng.lat && before.lng === newLatLng.lng) {
    return { nodeId: node.id, before, after: before, cables: [] }
  }

  // Snapshot previo de las rutas de cables conectados
  const connected = deps.cables.filter((c) => {
    const o = c.origenId || c.origen
    const d = c.destinoId || c.destino
    return o === node.id || d === node.id
  })

  const snapshots: Array<{ id: string; beforeRuta: PuntoRuta[] }> = connected
    .filter((c) => Array.isArray(c.ruta) && c.ruta.length > 0)
    .map((c) => ({
      id: c.id,
      beforeRuta: JSON.parse(JSON.stringify(c.ruta)) as PuntoRuta[],
    }))

  await deps.updateNode({
    ...(node as any),
    lat: newLatLng.lat,
    lng: newLatLng.lng,
  } as AnyNode)

  const cambios: MoverNodoResultado['cables'] = []

  for (const cable of connected) {
    const origenId = cable.origenId || cable.origen
    const isOrigen = origenId === node.id
    if (!Array.isArray(cable.ruta) || cable.ruta.length === 0) continue

    const nuevaRuta = [...cable.ruta]
    if (isOrigen) {
      nuevaRuta[0] = { ...nuevaRuta[0], lat: newLatLng.lat, lng: newLatLng.lng }
    } else {
      nuevaRuta[nuevaRuta.length - 1] = {
        ...nuevaRuta[nuevaRuta.length - 1],
        lat: newLatLng.lat,
        lng: newLatLng.lng,
      }
    }

    const snap = snapshots.find((s) => s.id === cable.id)
    if (!snap) continue

    try {
      await deps.updateCable({ ...(cable as any), ruta: nuevaRuta })
      cambios.push({
        id: cable.id,
        beforeRuta: snap.beforeRuta,
        afterRuta: JSON.parse(JSON.stringify(nuevaRuta)) as PuntoRuta[],
      })
    } catch (cableErr) {
      // Revertir posición del nodo para mantener consistencia visual
      try {
        await deps.updateNode({
          ...(node as any),
          lat: before.lat,
          lng: before.lng,
        } as AnyNode)
      } catch {
        /* ignore secondary error */
      }
      throw cableErr
    }
  }

  return {
    nodeId: node.id,
    before,
    after: { lat: newLatLng.lat, lng: newLatLng.lng },
    cables: cambios,
  }
}
